import React, {
	createContext,
	type ElementType,
	PureComponent,
	type ReactNode,
	startTransition,
} from 'react';

import memoizeOne, { type MemoizedFn } from 'memoize-one';

import noop from '@atlaskit/ds-lib/noop';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import Portal from '@atlaskit/portal';

import Blanket from '../styled/blanket';

export type TargetRef = (element: HTMLElement | null | undefined) => void;
export type GetTargetRef = (
	/**
	 * The `name` prop passed to the corresponding `SpotlightTarget`
	 *
	 * This is used as a key into the `targets` map owned by `SpotlightManager`
	 * because the `SpotlightManager` stores the target nodes for
	 * descendant `SpotlightTarget` instances.
	 */
	name: string,
) => TargetRef;

const dest = createContext<GetTargetRef | undefined>(undefined);
const TargetConsumer: React.Consumer<GetTargetRef | undefined> = dest.Consumer;
const TargetProvider: React.Provider<GetTargetRef | undefined> = dest.Provider;

const SpotlightContext: React.Context<{
	opened: () => void;
	closed: () => void;
	targets: {
		[key: string]: HTMLElement | undefined;
	};
}> = createContext<{
	opened: () => void;
	closed: () => void;
	targets: {
		[key: string]: HTMLElement | undefined;
	};
}>({
	opened: noop,
	closed: noop,
	targets: {},
});

const SpotlightStateConsumer: React.Consumer<{
	opened: () => void;
	closed: () => void;
	targets: {
		[key: string]: HTMLElement | undefined;
	};
}> = SpotlightContext.Consumer;
const SpotlightStateProvider: React.Provider<{
	opened: () => void;
	closed: () => void;
	targets: {
		[key: string]: HTMLElement | undefined;
	};
}> = SpotlightContext.Provider;

export { TargetConsumer };

export { SpotlightContext, SpotlightStateConsumer as SpotlightConsumer };

interface SpotlightManagerProps {
	/**
	 * Boolean prop for toggling blanket transparency.
	 */
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	blanketIsTinted?: boolean;
	/**
	 * Typically the app, or a section of the app.
	 */
	children: ReactNode;
	// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
	/**
	 * @deprecated
	 * Component is deprecated and will be removed in the future.
	 */
	// eslint-disable-next-line @repo/internal/react/consistent-props-definitions
	component?: ElementType;
	/**
	 * Handler function to be called when the blanket is clicked.
	 */
	onBlanketClicked?: () => void;
}

const Container = ({
	component: Wrapper,
	children,
}: {
	component: ElementType;
	children: ReactNode;
}) => <Wrapper>{children}</Wrapper>;

/**
 * __Spotlight manager__
 *
 * A spotlight manager manages the visibility of spotlights used to introduce new features to users through focused messages or multi-step tours.
 *
 * - [Examples](https://atlassian.design/components/onboarding/examples)
 * - [Code](https://atlassian.design/components/onboarding/code)
 * - [Usage](https://atlassian.design/components/onboarding/usage)
 *
 * @deprecated Use `@atlaskit/spotlight` instead.
 */
// eslint-disable-next-line @repo/internal/react/no-class-components
export default class SpotlightManager extends PureComponent<
	SpotlightManagerProps,
	{
		spotlightCount: number;
		targets: { [key: string]: HTMLElement | void };
	}
> {
	static defaultProps = {
		blanketIsTinted: true,
	};

	componentDidMount(): void {
		if (
			typeof process !== 'undefined' &&
			process.env.NODE_ENV !== 'production' &&
			!process.env.CI
		) {
			if (this.props.component) {
				// eslint-disable-next-line no-console
				console.warn(
					`Atlaskit: The SpotlightManager 'component' prop is deprecated. Please wrap the SpotlightManager in the component instead.`,
				);
			}
		}
	}

	state = {
		spotlightCount: 0,
		targets: {},
	};

	/*
	 * When enabling React Streaming in Confluence, a
	 * "This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering"
	 * error happens.
	 * This is to fix this error by wrapping the state update in startTransition as suggested by React: https://react.dev/errors/421?invariant=421
	 */
	getTargetRef: (name: string) => (element: HTMLElement | null | undefined) => void = (
		name: string,
	) =>
		(element: HTMLElement | null | undefined): void => {
			startTransition(() => {
				this.setState((state) => ({
					targets: {
						...state.targets,
						[name]: element || undefined,
					},
				}));
			});
		};

	spotlightOpen = (): void => {
		this.setState((state) => ({ spotlightCount: state.spotlightCount + 1 }));
	};

	spotlightClose = (): void => {
		this.setState((state) => ({ spotlightCount: state.spotlightCount - 1 }));
	};

	getStateProviderValue: MemoizedFn<
		(
			this: any,
			targets: any,
		) => {
			opened: () => void;
			closed: () => void;
			targets: any;
		}
	> = memoizeOne(
		(
			targets: any,
		): {
			opened: () => void;
			closed: () => void;
			targets: any;
		} => ({
			opened: this.spotlightOpen,
			closed: this.spotlightClose,
			targets,
		}),
	);

	render(): React.JSX.Element {
		const { blanketIsTinted, children, component: Tag, onBlanketClicked } = this.props;

		const isActive = this.state.spotlightCount > 0;

		return (
			<SpotlightStateProvider value={this.getStateProviderValue(this.state.targets)}>
				<TargetProvider value={this.getTargetRef}>
					<Container component={Tag || React.Fragment}>
						<ExitingPersistence>
							{isActive && (
								<FadeIn duration="medium">
									{({ ref, className, style }) => (
										<Portal zIndex={700}>
											<Blanket
												ref={ref}
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
												className={className}
												style={style}
												isTinted={blanketIsTinted}
												onBlanketClicked={onBlanketClicked}
											/>
										</Portal>
									)}
								</FadeIn>
							)}
						</ExitingPersistence>
						{children}
					</Container>
				</TargetProvider>
			</SpotlightStateProvider>
		);
	}
}
